/**
 * A note item's decision rung, as its card states it (#15565) — split out
 * of `record-page-model.ts` (#16759), which the file-size ratchet caps.
 *
 * Pure and client-safe: no Prisma, no `server-only`.
 */

import {
  type DecisionFormalityRead,
  formalityFromEvidence,
} from '@/lib/decisions/formality'
import { NOTETAKER_EVIDENCE_KIND } from '@/lib/evidence-kind-vocabulary'
import { DEFAULT_BELIEF_EVIDENCE_STANCE } from '@/lib/graph-consolidation/vocabulary'
import type { MeetingRecordItem } from '@/lib/meetings/record-item-model'

/**
 * A decision card's rung, read from the one Evidence row the card IS
 * (#15565). An EXTRACTION card's id is its `Evidence.id` and its quote that
 * row's excerpt, written by the note taker; a hand-typed decision has no
 * Evidence behind it and so reads `unknown`. A dismissed reading is not
 * live Evidence and counts for nothing.
 */
export function decisionCardFormality(
  row: MeetingRecordItem,
): DecisionFormalityRead {
  return formalityFromEvidence(
    row.source === 'EXTRACTION' && row.state !== 'DISMISSED'
      ? [
          {
            id: row.id,
            kind: NOTETAKER_EVIDENCE_KIND,
            source: 'INFERRED',
            commitmentLevel: row.commitmentLevel ?? null,
            stance: DEFAULT_BELIEF_EVIDENCE_STANCE,
            dismissedAt: null,
            excerpt: row.quotedText,
            actor: { personId: null, name: null, notetaker: true },
          },
        ]
      : [],
  )
}

/**
 * An accepted decision's card, with the rung its decision stands at
 * (#16759). The card's own rung reads only the note taker's quote, so an
 * accepted decision — which accepting records `agreed` (ADR-0134 § 3, as
 * amended by #16749) — read "Decision language heard" in its list while its
 * page said "Agreed". `rungs` is keyed by the card's `decisionId`; a card
 * with no entry keeps the rung it had.
 */
export function withDecisionRungs<
  T extends {
    kind: string | null
    state: string
    decisionId?: string | null
    formality?: DecisionFormalityRead | null
  },
>(cards: readonly T[], rungs: ReadonlyMap<string, DecisionFormalityRead>): T[] {
  return cards.map(card => {
    if (card.kind !== 'DECISION' || card.state !== 'ACCEPTED') return card
    const rung =
      card.decisionId == null ? undefined : rungs.get(card.decisionId)
    return rung === undefined ? card : { ...card, formality: rung }
  })
}

/** The decisions whose rung an accepted decision's card should state. */
export function acceptedDecisionIds(
  cards: readonly {
    kind: string | null
    state: string
    decisionId?: string | null
  }[],
): string[] {
  return [
    ...new Set(
      cards.flatMap(card =>
        card.kind === 'DECISION' &&
        card.state === 'ACCEPTED' &&
        card.decisionId != null
          ? [card.decisionId]
          : [],
      ),
    ),
  ]
}
